import {
	isUserProfileInitialized,
	useUserProfileContext
} from 'lib/contexts/userProfile.context'
import {
	ROLE_AFFILIATE,
	ROLE_DESTINATION_MANAGER,
	ROLE_HOTELIER,
	ROLE_PLANNER,
	ROLE_SUPPLIER
} from 'lib/helpers/roles'
import api from 'lib/interceptor/axios.interceptor'
import { ILocation } from 'models/location'
import React, { useEffect, useState } from 'react'
import HSButton from 'components/button'
import HSTextField from 'components/textField'
import HSCheckbox from 'components/checkbox'
import LocationSelector from 'components/locationSelector'
import Loader from 'components/loader'
import accountTypeOptions from 'lib/helpers/accountTypes'

const viewModes = {
	role: 'role',
	accountType: 'accountType',
	address: 'address',
	jobsToBeDone: 'jobsToBeDone',
	wait: 'wait'
}

const roleSelectionOptions = [
	{
		id: ROLE_PLANNER,
		title: 'Planner',
		description:
			'I create & send RFPs and book meetings & events for my organization or on behalf of my clients',
		hasJobsToBeDone: true
	},
	{
		id: ROLE_HOTELIER,
		title: 'On-Property Hotel Sales/Lead Catcher',
		description:
			'I assign and/or submit proposals as part of a hotel sales team',
		hasJobsToBeDone: false
	},
	{
		id: ROLE_DESTINATION_MANAGER,
		title: 'Destination Management Organization or CVB',
		description:
			'I suggest hotels and facilitate groups and meetings bookings for my city or destination',
		hasJobsToBeDone: false
	},
	{
		id: ROLE_AFFILIATE,
		title: 'Sales Affiliate',
		description:
			'I suggest hotels and facilitate groups and meetings bookings for member hotels in my portfolio',
		hasJobsToBeDone: false
	},
	{
		id: ROLE_SUPPLIER,
		title: 'Hotel Chain National Sales, Global Sales',
		description:
			'I suggest hotels and facilitate groups and meetings bookings for hotels in my chain',
		hasJobsToBeDone: false
	}
]

const jobsToBeDoneOptions = {
	[ROLE_PLANNER]: [
		'Save time during the RFP process',
		'Receive higher response rates when requesting proposals from hotels',
		'Increase the number of meetings/events I can source',
		'Compare proposals, download reports, and communicate cost savings',
		'Have all of my meeting sourcing history organized in one place',
		'Stay in control of the process (versus relying on third parties)'
	]
}

const SelectRoleView = () => {
	const { userProfile } = useUserProfileContext()
	const [accountState, setAccountState] = useState<{
		viewMode: string
		role: string
		location: ILocation | null
		address: Record<string, string>
		accountType?: string
		jobsToBeDone?: string[]
		organizationId?: string
	}>({
		viewMode: userProfile?.role ? viewModes.accountType : viewModes.role,
		role: userProfile?.role || '',
		location: null,
		address: { address: '', city: '', state: '', zip: '', country: '' },
		accountType: userProfile?.accountType || '',
		jobsToBeDone: [],
		organizationId: userProfile?.organizationId || ''
	})
	const [location, setLocation] = useState({})
	const [isAddressInvalid, setIsAddressInvalid] = useState(true)

	useEffect(() => {
		setLocation({
			name: accountState.address.address
		})
		setIsAddressInvalid(
			Object.keys(accountState.address).some(k => !accountState.address[k])
		)
	}, [accountState.address])

	const selectRole = (role: string) => {
		setAccountState(s => {
			return {
				...s,
				viewMode: viewModes.wait
			}
		})

		if (userProfile) {
			api
				.post(`/api/userprofiles/${userProfile.id}/selectrole/${role}`)
				.then(r => {
					if (role !== ROLE_PLANNER) {
						window.setTimeout(() => {
							if (!userProfile.accountType) {
								window.location.reload()
							}
						}, 3000)
					} else {
						setAccountState(s => {
							return {
								...s,
								role: role,
								organizationId: r.data.organizationId,
								viewMode: viewModes.accountType
							}
						})
					}
				})
				.catch(() => {
					// `invalid user profile id '${userProfile.id}'`
					// 'user role cannot be changed'
					// 'role is required'
					// `invalid role value '${role}'`
				})
		}
	}

	const selectAccountType = (accountType: string) => {
		setAccountState(s => {
			return {
				...s,
				accountType: accountType,
				viewMode: viewModes.address
			}
		})
	}

	const onChangeLocation = (location: ILocation) => {
		if (location.addressComponents) {
			const { streetNumber, route, city, state, zip, country } =
				location.addressComponents
			let v: Record<string, string> = {}
			if (route) {
				v.address = `${streetNumber ? `${streetNumber} ` : ''}${route}`
			}
			if (city) {
				v.city = city
			}
			if (state) {
				v.state = state
			}
			if (zip) {
				v.zip = zip
			}
			if (country) {
				v.country = country
			}
			setAccountState({
				...accountState,
				address: { ...v }
			})
		}
	}

	const onChangeAddress = (name: string, value: string) => {
		setAccountState(s => {
			return {
				...s,
				address: {
					...s.address,
					[name]: value
				}
			}
		})
	}

	const onSaveAddress = () => {
		api
			.put(`/api/organizations/${accountState.organizationId}/intent`, {
				source: 'https://hopskip.com/web-client/2.0',
				action: { type: 'mergeProperties' },
				value: Object.keys(accountState.address).map(k => {
					return { name: k, value: accountState.address[k] }
				})
			})
			.then(() => {
				setAccountState(s => {
					return {
						...s,
						viewMode: viewModes.jobsToBeDone
					}
				})
			})
	}

	const toggleJobToBeDone = (jobToBeDone: string, checked: boolean) => {
		setAccountState(s => {
			return {
				...s,
				jobsToBeDone: checked
					? [...(s.jobsToBeDone ?? []), jobToBeDone]
					: s.jobsToBeDone?.filter(j => j !== jobToBeDone)
			}
		})
	}

	const onSaveJtbd = () => {
		if (userProfile) {
			api
				.post(
					`/api/userprofiles/${userProfile.id}/selectaccounttype/${accountState.accountType}`,
					{
						...accountState,
						jobsToBeDone: accountState.jobsToBeDone?.sort((c, n) =>
							c > n ? 1 : -1
						)
					}
				)
				.then(() => {
					window.setTimeout(() => {
						if (!userProfile.accountType) {
							window.location.reload()
						}
					}, 3000)
				})
		}
	}

	return (
		<div className='flex min-h-screen items-center justify-center bg-gray-50'>
			<div className='max-w-4xl p-6'>
				{accountState.viewMode === viewModes.role && (
					<div className='rounded-lg bg-white p-8 shadow-lg'>
						<div className='mb-8 text-center'>
							<img
								src='/images/logo-h-text-primary.png'
								alt='Hopskip'
								className='mx-auto mb-4 h-16'
							/>
							<h2 className='mb-2 text-2xl font-bold text-gray-900'>
								Welcome to Hopskip!
							</h2>
							<p className='text-gray-600'>
								Please select your role to get started
							</p>
						</div>

						<div className='space-y-4'>
							{roleSelectionOptions.map(selectionRole => (
								<button
									key={selectionRole.id}
									color='light'
									className='w-full rounded-lg border border-gray-200 p-4 text-left hover:border-primary-500 hover:bg-gray-50'
									onClick={() => selectRole(selectionRole.id)}
								>
									<div className='flex flex-col gap-1'>
										<div>
											<div className='font-semibold text-gray-900'>
												{selectionRole.title}
											</div>
											<div className='text-sm text-gray-500'>
												{selectionRole.description}
											</div>
										</div>
									</div>
								</button>
							))}
						</div>
					</div>
				)}

				{accountState.viewMode === viewModes.accountType && (
					<div className='rounded-lg bg-white p-8 shadow-lg'>
						<div className='mb-8 text-center'>
							<h3 className='mb-2 text-xl font-bold text-gray-900'>
								What type of organization are you?
							</h3>
							<p className='text-gray-600'>Select one option</p>
						</div>

						<div className='space-y-4'>
							{accountTypeOptions
								.filter(o => o.description)
								.map((accountType, index) => (
									<button
										key={index}
										color='light'
										className='w-full rounded-lg border border-gray-200 p-4 text-left hover:border-primary-300'
										onClick={() => selectAccountType(accountType.value)}
									>
										<div>
											<div className='font-semibold text-gray-900'>
												{accountType.name}
											</div>
											<div className='text-sm text-gray-500'>
												{accountType.description}
											</div>
										</div>
									</button>
								))}
						</div>
					</div>
				)}

				{accountState.viewMode === viewModes.address && (
					<div className='rounded-lg bg-white p-8 shadow-lg'>
						<div className='mb-8 text-center'>
							<h3 className='mb-2 text-xl font-bold text-gray-900'>
								Please enter your business address
							</h3>
						</div>

						<div className='space-y-6'>
							<div>
								<LocationSelector
									value={location as ILocation}
									onChange={onChangeLocation}
									customPlaceholder='Enter your address...'
									isInvalid={!accountState.address.address}
								/>
							</div>

							<div className='grid grid-cols-1 gap-4'>
								<HSTextField
									label='Street Address'
									value={accountState.address.address}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										onChangeAddress('address', e.target.value)
									}
									placeholder='123 Main Street'
								/>
								<div className='grid grid-cols-2 gap-4'>
									<HSTextField
										label='City'
										value={accountState.address.city}
										onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
											onChangeAddress('city', e.target.value)
										}
										placeholder='City'
									/>
									<HSTextField
										label='State'
										value={accountState.address.state}
										onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
											onChangeAddress('state', e.target.value)
										}
										placeholder='State'
									/>
								</div>
								<div className='grid grid-cols-2 gap-4'>
									<HSTextField
										label='ZIP Code'
										value={accountState.address.zip}
										onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
											onChangeAddress('zip', e.target.value)
										}
										placeholder='12345'
									/>
									<HSTextField
										label='Country'
										value={accountState.address.country}
										onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
											onChangeAddress('country', e.target.value)
										}
										placeholder='Country'
									/>
								</div>
							</div>

							<HSButton
								color='primary'
								size='lg'
								className='w-full'
								onClick={onSaveAddress}
								disabled={isAddressInvalid}
							>
								Continue
							</HSButton>
						</div>
					</div>
				)}

				{accountState.viewMode === viewModes.jobsToBeDone && (
					<div className='rounded-lg bg-white p-8 shadow-lg'>
						<div className='mb-8 text-center'>
							<h3 className='mb-2 text-xl font-bold text-gray-900'>
								How would you like Hopskip to help you in your job?
							</h3>
							<p className='text-gray-600'>Select all that apply</p>
						</div>

						<div className='mb-8 space-y-4'>
							{jobsToBeDoneOptions[
								accountState.role as keyof typeof jobsToBeDoneOptions
							]?.map((job: string, index: number) => (
								<div key={index} className='flex items-center gap-4'>
									<HSCheckbox
										checked={accountState.jobsToBeDone?.includes(job) || false}
										onChange={e => toggleJobToBeDone(job, e.target.checked)}
									/>
									<label className='cursor-pointer text-gray-700'>{job}</label>
								</div>
							))}
						</div>

						<HSButton
							color='primary'
							size='lg'
							className='w-full'
							onClick={onSaveJtbd}
							disabled={!accountState.jobsToBeDone?.length}
						>
							Save
						</HSButton>
					</div>
				)}

				{accountState.viewMode === viewModes.wait && (
					<div className='rounded-lg bg-white p-8 shadow-lg'>
						{(!isUserProfileInitialized(userProfile) && (
							<div className='flex flex-col gap-1'>
								<Loader size='lg' />
								Please wait...
							</div>
						)) ||
							(ROLE_HOTELIER === accountState.role && (
								<div>
									You can reach out using the chat tool in the lower left corner
									if you need to be associated with any properties.
								</div>
							)) || <div>Taking you to your dashboard...</div>}
					</div>
				)}
			</div>
		</div>
	)
}

export default SelectRoleView
